// src/services/sdkLogConfig.js
// Quiets the matrix-js-sdk + matrix-rust-crypto (WASM) console logging in production.
//
// Why this exists (NED-4003 "Excessive Console Errors and Rapid Log Generation During
// Login"): the matrix-js-sdk loglevel logger defaults to DEBUG and the app never raised
// it, so every login dumped the rust crypto-store schema migrations (INFO), the
// key-backup-downloader status (INFO), dehydration steps (INFO) and a per-event
// decryption WARNING for every historical message a fresh device can't decrypt — tens of
// thousands of lines / multiple MB in the console once key backup isn't available.
//
// The SDK singleton logger is ALSO the `JsLogger` that matrix-js-sdk hands to the
// rust-crypto WASM (StoreHandle.open / OlmMachine.initFromStore), so a single setLevel()
// governs BOTH the JS-side SDK logs and the WASM `tracing` output — there is no separate
// wasm-to-console subscriber to silence in matrix-sdk-crypto-wasm 17.x.
//
// Level policy: production -> WARN, development -> DEBUG, overridable via
// localStorage['TI-M_LOG_LEVEL'] (the same key the app's own logger reads, so a single
// override dials both). This is intentionally NOT identical to src/utils/logger.js, which
// defaults to ERROR in production: the SDK is kept one level more verbose (WARN) so the
// genuinely diagnostic warnings survive — "Failed to decrypt a room event", "Error
// decrypting event", pending /keys/query — while the routine INFO bookkeeping (crypto-store
// migrations, key-backup-downloader status, dehydration) is dropped. Those remaining
// decryption warnings point at the real cause (key backup not trusted) and should be
// resolved there, not by raising this to ERROR.
//
// This file lives in src/services/** — the encapsulation layer that is permitted to
// import matrix-js-sdk directly (see eslint no-restricted-imports config).

import { logger as sdkLogger } from 'matrix-js-sdk/lib/logger';

// App-level names (src/utils/logger.js) that map 1:1 onto loglevel's level strings.
const VALID_LEVELS = ['error', 'warn', 'info', 'debug'];

const resolveLevel = () => {
  try {
    const override = localStorage.getItem('TI-M_LOG_LEVEL');
    if (override && VALID_LEVELS.includes(override.toLowerCase())) {
      return override.toLowerCase();
    }
  } catch {
    // localStorage may be unavailable (private mode / blocked) — fall back to env default.
  }
  return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
};

/**
 * Apply the resolved log level to the matrix-js-sdk singleton logger.
 *
 * Must run BEFORE the first initRustCrypto() so the WASM picks up the already-leveled
 * JsLogger. Safe to call repeatedly (loglevel.setLevel is idempotent); callers may invoke
 * it from every createClient() path defensively.
 *
 * Note: child loggers created later via logger.getChild() (e.g. the "sync" child) reset to
 * the SDK default — but the high-volume sources (rust-crypto tracing via the JsLogger and
 * the decryption warnings in models/event.js) all use this singleton, so this is sufficient.
 */
export function configureSdkLogging() {
  try {
    // persist=false: don't write loglevel's own localStorage key; we re-apply on each load.
    sdkLogger.setLevel(resolveLevel(), false);
  } catch {
    // Never let logging configuration break client startup.
  }
}
